import * as styles from "./DatePickerGrid.css";

import type { JSX, KeyboardEvent } from "react";
import { useEffect, useRef, useState } from "react";

import { useLingui } from "@lingui/react/macro";

import { useDatePickerContext } from "./DatePicker.context";
import type { DatePickerGridProps } from "./DatePicker.types";
import { DatePickerDay } from "./DatePickerDay";
import {
  type CalendarDate,
  addMonths,
  compareDate,
  daysInMonth,
  today,
} from "./dateMath";

const WEEKS = 6;
const DAYS_PER_WEEK = 7;

function moveDay(
  { year, month, day }: CalendarDate,
  deltaDays: number,
): CalendarDate {
  const date = new Date(year, month, day + deltaDays);

  return {
    year: date.getFullYear(),
    month: date.getMonth(),
    day: date.getDate(),
  };
}

function moveMonthClamped(
  { year, month, day }: CalendarDate,
  deltaMonths: number,
): CalendarDate {
  const next = addMonths(year, month, deltaMonths);

  return {
    year: next.year,
    month: next.month,
    day: Math.min(day, daysInMonth(next.year, next.month)),
  };
}

function weekStartOffset(date: CalendarDate, weekStartsOn: 0 | 1): number {
  const dow = new Date(date.year, date.month, date.day).getDay();

  return (dow - weekStartsOn + DAYS_PER_WEEK) % DAYS_PER_WEEK;
}

function buildCells(
  viewYear: number,
  viewMonth: number,
  weekStartsOn: 0 | 1,
): CalendarDate[] {
  const leading = weekStartOffset(
    { year: viewYear, month: viewMonth, day: 1 },
    weekStartsOn,
  );
  const totalCells = WEEKS * DAYS_PER_WEEK;

  return Array.from({ length: totalCells }, (_, index) =>
    moveDay({ year: viewYear, month: viewMonth, day: 1 }, index - leading),
  );
}

function isSameDay(a: CalendarDate, b: CalendarDate): boolean {
  return a.year === b.year && a.month === b.month && a.day === b.day;
}

function defaultActiveDay(
  viewYear: number,
  viewMonth: number,
  selected: CalendarDate | null,
): CalendarDate {
  if (selected && selected.year === viewYear && selected.month === viewMonth) {
    return selected;
  }

  const now = today();

  if (now.year === viewYear && now.month === viewMonth) {
    return now;
  }

  return { year: viewYear, month: viewMonth, day: 1 };
}

export function DatePickerGrid({
  monthOffset = 0,
}: DatePickerGridProps = {}): JSX.Element {
  const { t } = useLingui();

  const {
    viewYear,
    viewMonth,
    selected,
    range,
    selectedRange,
    openEnded,
    hoveredDay,
    setHoveredDay,
    min,
    max,
    weekStartsOn,
    locale,
    goToMonth,
  } = useDatePickerContext();

  const { year: gridYear, month: gridMonth } = addMonths(
    viewYear,
    viewMonth,
    monthOffset,
  );

  const [rangeStart, rangeEnd] = selectedRange;

  const previewEnd =
    range &&
    !openEnded &&
    null !== rangeStart &&
    null === rangeEnd &&
    null !== hoveredDay
      ? compareDate(hoveredDay, rangeStart) >= 0
        ? hoveredDay
        : null
      : rangeEnd;

  const gridRef = useRef<HTMLDivElement>(null);
  const pendingFocusRef = useRef<CalendarDate | null>(null);

  const [activeDay, setActiveDay] = useState<CalendarDate>(() =>
    defaultActiveDay(gridYear, gridMonth, selected),
  );

  useEffect(() => {
    if (pendingFocusRef.current) {
      const pending = pendingFocusRef.current;

      pendingFocusRef.current = null;
      setActiveDay(pending);
      focusDay(pending);

      return;
    }

    setActiveDay(defaultActiveDay(gridYear, gridMonth, selected));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridYear, gridMonth]);

  function focusDay(date: CalendarDate) {
    gridRef.current
      ?.querySelector<HTMLButtonElement>(
        `[data-year="${date.year}"][data-month="${date.month}"][data-day="${date.day}"]`,
      )
      ?.focus({ preventScroll: true });
  }

  function navigateTo(next: CalendarDate) {
    if (next.year === gridYear && next.month === gridMonth) {
      setActiveDay(next);
      focusDay(next);

      return;
    }

    pendingFocusRef.current = next;

    const monthDelta = (next.year - gridYear) * 12 + (next.month - gridMonth);

    goToMonth(monthDelta);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    switch (event.key) {
      case "ArrowLeft":
        navigateTo(moveDay(activeDay, -1));
        break;
      case "ArrowRight":
        navigateTo(moveDay(activeDay, 1));
        break;
      case "ArrowUp":
        navigateTo(moveDay(activeDay, -DAYS_PER_WEEK));
        break;
      case "ArrowDown":
        navigateTo(moveDay(activeDay, DAYS_PER_WEEK));
        break;
      case "Home":
        navigateTo(
          moveDay(activeDay, -weekStartOffset(activeDay, weekStartsOn)),
        );
        break;
      case "End":
        navigateTo(
          moveDay(
            activeDay,
            DAYS_PER_WEEK - 1 - weekStartOffset(activeDay, weekStartsOn),
          ),
        );
        break;
      case "PageUp":
        navigateTo(moveMonthClamped(activeDay, -1));
        break;
      case "PageDown":
        navigateTo(moveMonthClamped(activeDay, 1));
        break;
      default:
        return;
    }

    event.preventDefault();
  }

  const cells = buildCells(gridYear, gridMonth, weekStartsOn);
  const now = today();

  const weekdayLabels = Array.from({ length: DAYS_PER_WEEK }, (_, index) => {
    const dow = (weekStartsOn + index) % DAYS_PER_WEEK;
    // 2023-01-01 was a Sunday, a stable reference to get weekday names from.
    const reference = new Date(2023, 0, 1 + dow);

    return new Intl.DateTimeFormat(locale, { weekday: "short" }).format(
      reference,
    );
  });

  return (
    <div
      ref={gridRef}
      role="grid"
      aria-label={t`Calendar`}
      className={styles.grid({ range })}
      onKeyDown={handleKeyDown}
      onPointerLeave={range ? () => setHoveredDay(null) : undefined}
    >
      <div role="row" className={styles.row}>
        {weekdayLabels.map((label, index) => (
          <span key={index} role="columnheader" className={styles.columnHeader}>
            {label}
          </span>
        ))}
      </div>

      {Array.from({ length: WEEKS }, (_, week) => (
        <div key={week} role="row" className={styles.row}>
          {cells
            .slice(week * DAYS_PER_WEEK, week * DAYS_PER_WEEK + DAYS_PER_WEEK)
            .map((cell) => (
              <DatePickerDay
                key={`${cell.year}-${cell.month}-${cell.day}`}
                year={cell.year}
                month={cell.month}
                day={cell.day}
                outsideMonth={
                  cell.month !== gridMonth || cell.year !== gridYear
                }
                selected={
                  (selected !== null && isSameDay(cell, selected)) ||
                  (null !== rangeStart && isSameDay(cell, rangeStart)) ||
                  (null !== previewEnd && isSameDay(cell, previewEnd))
                }
                inRange={
                  null !== rangeStart &&
                  null !== previewEnd &&
                  compareDate(cell, rangeStart) >= 0 &&
                  compareDate(cell, previewEnd) <= 0
                }
                rangeStart={null !== rangeStart && isSameDay(cell, rangeStart)}
                rangeEnd={null !== previewEnd && isSameDay(cell, previewEnd)}
                today={isSameDay(cell, now)}
                active={isSameDay(cell, activeDay)}
                disabled={
                  (min !== null && compareDate(cell, min) < 0) ||
                  (max !== null && compareDate(cell, max) > 0)
                }
              />
            ))}
        </div>
      ))}
    </div>
  );
}
